import React, { useState, useEffect } from 'react';
import Modal from '../../components/modal/modal';
import Input from '../../components/input/Input';
import Card from '../../components/card/Card';

interface RecalculateCommissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRecalculate: (ratePct: number, expensePct: number, referralPct: number) => Promise<void>;
  currentRatePct: number;
  currentExpensePct: number;
  currentReferralPct: number;
  hasReferrer: boolean;
}

const RecalculateCommissionModal: React.FC<RecalculateCommissionModalProps> = ({
  isOpen,
  onClose,
  onRecalculate,
  currentRatePct,
  currentExpensePct,
  currentReferralPct,
  hasReferrer,
}) => {
  const [ratePct, setRatePct] = useState('');
  const [expensePct, setExpensePct] = useState('');
  const [referralPct, setReferralPct] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setRatePct(currentRatePct.toString());
      setExpensePct(currentExpensePct.toString());
      setReferralPct(hasReferrer ? currentReferralPct.toString() : '0');
      setError(null);
    }
  }, [isOpen, currentRatePct, currentExpensePct, currentReferralPct, hasReferrer]);

  const validate = (): boolean => {
    const rate = parseFloat(ratePct);
    const expense = parseFloat(expensePct);
    const referral = parseFloat(referralPct);

    if (isNaN(rate) || isNaN(expense) || isNaN(referral)) {
      setError('Los porcentajes deben ser valores numéricos');
      return false;
    }
    if (rate < 0 || expense < 0 || referral < 0) {
      setError('Los porcentajes no pueden ser negativos');
      return false;
    }
    if (rate > 999.99 || expense > 999.99 || referral > 999.99) {
      setError('Los porcentajes no pueden superar 999,99');
      return false;
    }
    setError(null);
    return true;
  };

  const handleRecalculate = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onRecalculate(parseFloat(ratePct), parseFloat(expensePct), parseFloat(referralPct));
      onClose();
    } catch (err) {
      setError('Error al recalcular la comisión');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const isFormValid =
    ratePct.trim() !== '' &&
    expensePct.trim() !== '' &&
    !isNaN(parseFloat(ratePct)) &&
    !isNaN(parseFloat(expensePct)) &&
    parseFloat(ratePct) >= 0 &&
    parseFloat(expensePct) >= 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Recalcular comisión"
      primaryText="Recalcular"
      primaryVariant="primary"
      onPrimaryAction={handleRecalculate}
      primaryActionDisabled={!isFormValid}
      loadingPrimaryBtn={loading}
      secondaryText="Cancelar"
      secondaryVariant="contained"
      onSecondaryAction={handleClose}
      width="450px"
    >
      <Card margin={{ top: '4' }} gap='16px'>
        <Card>
          <Input
            id="rate-pct"
            label="Tasa %"
            type="number"
            size="md"
            placeholder="Ingrese el porcentaje de tasa"
            value={ratePct}
            onChange={(e) => setRatePct(e.target.value)}
          />
        </Card>

        <Card margin={{ top: '3' }}>
          <Input
            id="expense-pct"
            label="Gasto %"
            type="number"
            size="md"
            placeholder="Ingrese el porcentaje de gasto"
            value={expensePct}
            onChange={(e) => setExpensePct(e.target.value)}
          />
        </Card>

        <Card margin={{ top: '3' }}>
          <Input
            id="referral-pct"
            label="% al referido"
            type="number"
            size="md"
            placeholder={hasReferrer ? 'Ingrese el porcentaje al referido' : '—'}
            value={referralPct}
            onChange={(e) => setReferralPct(e.target.value)}
            disabled={!hasReferrer}
          />
        </Card>

        {error && (
          <Card margin={{ top: '2' }}>
            <p style={{ color: '#DC2626', fontSize: '14px', margin: 0 }}>{error}</p>
          </Card>
        )}
      </Card>
    </Modal>
  );
};

export default RecalculateCommissionModal;
